import { useState } from "react";
import { Select } from "flowbite-react";
import { HiCheckCircle, HiChevronDown } from "react-icons/hi";

import { StageChecklist } from "./StageChecklist";
import { getRoleStatus } from "./pipelineRoles";

// Collapsible card for one pipeline role: pick the pipeline that plays the
// role, then tick the stages each group needs. Collapsed, it shows a one-line
// completion summary so the pipelines step reads as a short checklist.
export const PipelineRoleCard = ({
  role,
  pipelines,
  customizations,
  onUpdate,
  activeDealPipelineId,
}) => {
  const { configured, summary } = getRoleStatus(role, customizations);
  const [expanded, setExpanded] = useState(!configured);

  const selectedPipelineId = customizations?.[role.key]?.id || "";
  const selectedPipeline =
    pipelines.find((pipeline) => pipeline.id === selectedPipelineId) ?? null;
  const stages = [...(selectedPipeline?.stages ?? [])].sort(
    (a, b) => a.display_order - b.display_order,
  );

  const handlePipelineChange = (e) => {
    const pipeline = pipelines.find((p) => p.id === e.target.value);
    // Stage selections belong to the previous pipeline — clear them so stale
    // stages don't linger in the saved payload.
    const clearedGroups = Object.fromEntries(
      role.stageGroups.map((group) => [group.key, []]),
    );
    onUpdate({
      [role.key]: pipeline ? { id: pipeline.id, name: pipeline.label } : null,
      ...clearedGroups,
    });
  };

  return (
    <div className="rounded-lg border border-gray-200 bg-white">
      <button
        type="button"
        className="flex w-full items-center justify-between gap-3 px-4 py-3 text-left"
        onClick={() => setExpanded((prev) => !prev)}
      >
        <div>
          <p className="font-semibold text-black">{role.title}</p>
          <p
            className={`text-xs ${configured ? "text-gray-500" : "text-yellow-600"}`}
          >
            {summary}
          </p>
        </div>
        <div className="flex shrink-0 items-center gap-2">
          {configured ? (
            <HiCheckCircle className="h-5 w-5 text-green-500" />
          ) : (
            // Hollow dashed circle: checklist "todo" — an empty slot, not an
            // error and not a remove action.
            <span
              aria-hidden="true"
              className="h-5 w-5 rounded-full border-2 border-dashed border-gray-300"
            />
          )}
          <HiChevronDown
            className={`h-4 w-4 text-gray-400 transition-transform ${
              expanded ? "rotate-180" : ""
            }`}
          />
        </div>
      </button>

      {expanded && (
        <div className="space-y-4 border-t border-gray-100 px-4 py-4">
          <p className="text-xs text-gray-500">{role.description}</p>
          <div className="max-w-md">
            <Select
              id={`${role.key}-select`}
              value={selectedPipelineId}
              onChange={handlePipelineChange}
            >
              <option value="">Select a pipeline</option>
              {pipelines.map((pipeline) => (
                <option key={pipeline.id} value={pipeline.id}>
                  {pipeline.label}
                </option>
              ))}
            </Select>
            {role.warnIfSameAsActive &&
              selectedPipelineId &&
              selectedPipelineId === activeDealPipelineId && (
                <p className="mt-1 text-xs text-yellow-600">
                  {role.warnIfSameAsActive}
                </p>
              )}
          </div>

          {stages.length > 0 &&
            role.stageGroups.map((group) => (
              <StageChecklist
                key={group.key}
                group={group}
                stages={stages}
                selected={customizations?.[group.key]}
                onChange={(next) => onUpdate({ [group.key]: next })}
              />
            ))}
        </div>
      )}
    </div>
  );
};
